/* eslint-disable import/no-cycle */
import { Placement as PopoverPlacement } from '@popperjs/core';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { DisableAutoFocus } from '../../utils/AutoFocus';
import { generateId } from '../../utils/DomUtil';
import { EventUtilsOptOut, EventUtilsOptOutAttr, PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { StackingComponentProps } from '../../utils/StackingComponent';
import { AlertType as PopoverAlertType } from '../AlertStyles';
import { ButtonWidget } from '../Button/ButtonWidgetShim';
import { PopoverBase } from './PopoverBase';
import { PopoverTriggerStyleName } from './PopoverStyles';

export { PopoverAlertType };
export { PopoverPlacement };

/** https://x.com/mattpocockuk/status/1821926395380986219 */
type OtherWidth = (number & {});

export type PopoverProps = DisableAutoFocus & Pick<StackingComponentProps, 'portalEventTrapOverrides' | 'stackingRootId' | 'zIndex'> & {
  /**
  * Optionally adds an alert style to the popover.
  * Note: that supplying a value here will always show a title bar, even if `showTitleBar` is set to `false`.
  */
  alertType?: PopoverAlertType;

  /** If your popover contains text content which helps to explain the popover, pass in that element's id to this prop. */
  'aria-describedby'?: string;

  children?: never;

  /**
   * When true, the text and close button colors will be adjusted for a dark background.
   * Don't forget to set a dark background color in the `style` prop!
  */
  darkMode?: boolean;

  /** Will create `ButtonWidget`s in the footer which are aligned and spaced in a standardized way. */
  footerButtons?: Array<ButtonWidget['props']>;

  /**
   * When this prop is omitted, when the popover closes, it attempts to return focus to the element which was focused when the popover was opened.
   * If you would like to customize which element receives focus when the popover closes, return that element via this callback.
   * This may be useful e.g. if the previously-focused element has been removed from the DOM by the time the popover closes.
   */
  getElementToRefocus?: () => HTMLElement;

  /**
   * Where the popover should attempt to position itself, relative to its `triggerElement`.
   *
   *  * top/right/bottom/left: Indicate which side of the `triggerElement` to align to.
   *  * start/end: On vertical sides, start indicates the top and end indicates the bottom.
   *  * start/end: On horizontal sides, start indicates the left and end indicates the right.
   * @default ```'auto'```
   */
  placement?: PopoverPlacement;

  /** The content rendered inside of the popover. */
  popoverContent: React.ReactChild;

  /**
   * Whether or not to display a title bar in the popover.
   * Note: that supplying a value to `alertType` will always show a title bar, regardless of this value.
   */
  showTitleBar?: boolean;

  /** Added to the root `<div>` with the suffix `-PopoverWidget` */
  testId?: string;

  /** A title is required for accessiblity, even if not shown visually. */
  title: React.ReactChild;

  /**
   * The Element which triggers the popover, and to which the popover will be anchored —
   * generally this will be a `ButtonWidget`. Far-out, tubular cases may pass a plain `HTMLElement`, but we recommend sticking with the React paradigm.
   *
   * `aria-controls`, `aria-haspopup`, and `onClick` will be overridden on this trigger element.
   * Additionally, when passing a `ReactElement`, the component must forward any HTML attributes it receives in its `props` to its internal `<button>` element.
   */
  triggerElement: HTMLElement | React.ReactElement<React.HTMLAttributes<HTMLElement>>;

  /**
   * Sets the width of the popover.
   * Per TDLS, you should choose one of the following 3 widths:
   * * `200`
   * * `300`
   * * `400`
   * @default ```300```
   */
  width?: 200 | 300 | 400 | OtherWidth;
};

export type PopoverInstance = {
  /**
   * Toggles the popover.
   * @param open optional parameter, ```true``` to open the popover or ```false``` to close it.
   */
  toggle: (open?: boolean) => void
};

type PopoverState = { isVisible: boolean };
class PopoverComponent extends React.PureComponent<PropsAndElementAttrs<PopoverProps>, PopoverState> {
  public state: PopoverState = { isVisible: false };

  private triggerEl: HTMLElement | null = null;

  private readonly defaultPopoverId: string = generateId();
  private getPopoverId = (): string => this.props.id || this.defaultPopoverId;

  private readonly defaultTriggerId: string = generateId();
  private getTriggerId = (): string => {
    const { triggerElement } = this.props;
    return (triggerElement instanceof HTMLElement ? triggerElement.id : triggerElement.props.id) || this.defaultTriggerId;
  }

  public toggle(shown: boolean = !this.state.isVisible): void {
    if (shown && !this.triggerEl) {
      this.triggerEl = document.getElementById(this.getTriggerId());
    }
    this.setState({ isVisible: shown });
  }

  private handleClose = () => this.setState({ isVisible: false });
  private handleClick: EventListener = e => {
    this.triggerEl = e.currentTarget as HTMLElement;
    this.setState(({ isVisible }) => ({ isVisible: !isVisible }));
  }

  private updateTriggerAttrs(): void {
    const { triggerElement } = this.props;
    const { isVisible } = this.state;

    if (triggerElement instanceof HTMLElement) {
      triggerElement.addEventListener('click', this.handleClick); // this is a no-op in the case of re-adding a listener that's already added
      triggerElement.setAttribute(EventUtilsOptOutAttr, "true");
      triggerElement.setAttribute('aria-haspopup', 'dialog');

      if (isVisible) {
        triggerElement.setAttribute('aria-controls', this.getPopoverId());
      } else {
        triggerElement.removeAttribute('aria-controls');
      }
      this.triggerEl = triggerElement;
    }
  }

  public componentDidMount(): void {
    this.updateTriggerAttrs();
  }

  public componentDidUpdate(prevProps: PopoverProps, prevState: PopoverState): void {
    if (prevProps.triggerElement !== this.props.triggerElement || prevState.isVisible !== this.state.isVisible) {
      this.updateTriggerAttrs();
    }
  }

  public componentWillUnmount(): void {
    const { triggerElement } = this.props;
    if (triggerElement instanceof HTMLElement) {
      triggerElement.removeAttribute('aria-haspopup');
      triggerElement.removeAttribute('aria-controls');
      triggerElement.removeEventListener('click', this.handleClick);
      triggerElement.removeAttribute(EventUtilsOptOutAttr);
    }
  }

  public render(): React.ReactNode {
    const { triggerElement, ...popoverProps } = this.props;
    const { isVisible } = this.state;

    const clonedTrigger = triggerElement instanceof HTMLElement ? null : React.cloneElement(triggerElement, {
      ...EventUtilsOptOut,
      'aria-controls': isVisible ? this.getPopoverId() : undefined,
      'aria-haspopup': 'dialog',
      className: TypeStyle.classes(triggerElement.props.className, PopoverTriggerStyleName), // remove dropdown arrows from presumably-`ButtonWidget` child
      id: this.getTriggerId(),
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        this.handleClick(e as unknown as Event);
        triggerElement.props.onClick?.(e);
      },
    });

    return <>
      {clonedTrigger}
      {isVisible && this.triggerEl &&
        <PopoverBase {...popoverProps} id={this.getPopoverId()} onClose={this.handleClose} triggerElement={this.triggerEl} />
      }
    </>;
  }
}

// We migrated the PopoverWidget to a class component to support extending the `triggerElement` API to accept `HTMLElement`.
// We added this FC wrapper to maintain backwards compatibility with using a `PopoverInstance` `ref`.
export const PopoverWidget = React.forwardRef<PopoverInstance, PropsAndElementAttrs<PopoverProps>>((props, ref) => {
  const popoverInstance = React.useRef<PopoverComponent>(null);
  React.useImperativeHandle(ref, (): PopoverInstance => ({ toggle: shown => popoverInstance.current?.toggle(shown) }));
  return <PopoverComponent {...props} ref={popoverInstance} />;
});
PopoverWidget.displayName = 'PopoverWidget';
PopoverWidget.defaultProps = {
  placement: 'auto',
  width: 300,
  zIndex: 1,
};
